import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../shared/services/api';
import { useAuthBusiness } from './AuthBusinessContext';

import { getRequiredModules } from '../services/pageModuleConfig';

const ModuleAccessContext = createContext();

export const useModuleAccess = () => {
  const context = useContext(ModuleAccessContext);
  if (!context) {
    throw new Error('useModuleAccess must be used within a ModuleAccessProvider');
  }
  return context;
};

export const ModuleAccessProvider = ({ children }) => {
  const [moduleAccess, setModuleAccess] = useState({
    loading: true,
    hasSubscription: false,
    packageName: '',
    packageFeatures: [],
    userAddons: [],
    availableModules: [],
    totalModules: 0,
    accessibleModules: 0
  });

  const { selectedBusiness, currentUser, currentUserRole, isAuthenticated } = useAuthBusiness();

  const fetchModuleAccess = async () => {
    if (isAuthenticated === null || (isAuthenticated && !currentUser)) {
      if (process.env.NODE_ENV === 'development') {
        console.log('⏳ ModuleAccessContext: Waiting for authentication/user state...');
      }
      setModuleAccess(prev => ({ ...prev, loading: true }));
      return;
    }

    // 🛡️ FAILSAFE: If we are explicitly UNauthenticated, we must stop loading to allow redirects.
    if (isAuthenticated === false) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🚫 ModuleAccessContext: User is not authenticated.');
      }
      setModuleAccess(prev => ({ ...prev, loading: false, hasSubscription: false }));
      return;
    }

    try {
      setModuleAccess(prev => ({ ...prev, loading: true }));

      // Use business from context or fallback
      const currentBusiness = selectedBusiness || JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      
      // 🛡️ RE-VALIDATION: If subscription modules are empty/basic, try to fetch the LATEST business doc from Firestore
      // This is crucial for users who just got their package updated.
      let finalBusinessDoc = currentBusiness;
      if (isAuthenticated && currentBusiness?.id && (!currentBusiness?.subscription?.active_modules || currentBusiness?.subscription?.active_modules.length <= 1)) {
         try {
           const { doc, getDoc } = await import('firebase/firestore');
           const { db } = await import('../services/firebase/config'); // Adjust path
           if (process.env.NODE_ENV === 'development') {
             console.log('🔄 ModuleAccessContext: Re-validating business data from Firestore for:', currentBusiness.id);
           }
           
           // Try multiple sectors for re-validation as well
           const sectors = ['food_beverage', 'service', 'retail', 'hospitality'];
           let busSnap = null;
           for (const sector of sectors) {
              const busRef = doc(db, 'businesses', sector, 'businesses', currentBusiness.id);
              busSnap = await getDoc(busRef);
              if (busSnap.exists()) break;
           }

           if (busSnap && busSnap.exists()) {
              finalBusinessDoc = { id: busSnap.id, ...busSnap.data() };
              if (process.env.NODE_ENV === 'development') {
                console.log('✅ ModuleAccessContext: FRESH data loaded from Firestore. Active Modules:', finalBusinessDoc?.subscription?.active_modules);
              }
           }
         } catch (fError) {
           console.warn('⚠️ ModuleAccessContext: Re-validation failed, using local state.', fError.message);
         }
      }

      // 🛡️ CARI ROLE: Handle this BEFORE anything else to avoid subscription fetch loops
      const userRole = currentUserRole || currentUser?.role || currentUser?.user_type || localStorage.getItem('userRole');

      if (userRole === 'cari') {
        try {
          const { MODULE_REGISTRY } = await import('../services/moduleRegistry');
          if (process.env.NODE_ENV === 'development') {
            console.log('🚀 ModuleAccessContext: Cari user detected. Granting access.');
          }
          const cariModules = ['core_access', 'stock_basic'];
          const cariModuleObjects = cariModules.map(id => MODULE_REGISTRY[id]).filter(Boolean);

          setModuleAccess({
            loading: false,
            hasSubscription: true,
            packageName: 'Cari Portal Access',
            packageFeatures: ['limited_distribution'],
            userAddons: [],
            availableModules: cariModuleObjects,
            activeModuleIds: cariModules,
            totalModules: cariModules.length,
            accessibleModules: cariModules.length
          });
          return;
        } catch (e) {
          console.error('❌ Cari module import error:', e);
        }
      }

      if (!finalBusinessDoc || !finalBusinessDoc.id) {
        // 🛡️ RE-HYDRATION FIX: If we have a token but no business yet, wait.
        const hasToken = !!localStorage.getItem('token');
        const storedBusinessId = localStorage.getItem('selectedBusinessId');
        
        if (hasToken && (storedBusinessId || !isAuthenticated)) {
          if (process.env.NODE_ENV === 'development') {
             console.log('⏳ ModuleAccessContext: Waiting for business re-hydration...');
          }
          return; // Keep loading true
        }

        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ ModuleAccessContext: No business selected');
        }
        setModuleAccess({
          loading: false,
          hasSubscription: false,
          availableModules: [],
          activeModuleIds: [],
          packageFeatures: [],
          packageName: '',
          userAddons: []
        });
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 ModuleAccessContext: Processing subscription for', finalBusinessDoc.id);
      }

      // Import services dynamically
      const { MODULE_REGISTRY } = await import('../services/moduleRegistry');
      const { default: SubscriptionService } = await import('../services/subscriptionService');
      const { default: FirebasePackagesService } = await import('../services/firebase/packages');

      // --- 1. PRE-CHECK: Dynamic Modules from selectedBusiness (Optimized) ---
      if (finalBusinessDoc && finalBusinessDoc.subscription && finalBusinessDoc.subscription.status === 'active') {
        // Merge active_modules and add_ons
        const activeModules = finalBusinessDoc.subscription.active_modules || [];
        const addons = finalBusinessDoc.add_ons || finalBusinessDoc.subscription.add_ons || finalBusinessDoc.subscription.addons || [];
        
        // Use a Set to ensure unique IDs
        let combinedModuleIds = Array.from(new Set([...activeModules, ...addons, 'core_access']));

        // 🛡️ FRANCHISE BRANCH MANAGER FILTER:
        // Şube yöneticileri alt şube açamaz, qr menü builder/web sitesi yönetemez, fatura alamaz
        const effectiveUserRole = currentUserRole || currentUser?.role || currentUser?.user_type || localStorage.getItem('userRole');
        if (effectiveUserRole === 'branch_manager' || currentUser?.branch_id || selectedBusiness?.branch_id) {
          const restrictedForBranches = ['multi_branch', 'qr_menu', 'billing_subscription', 'social_media'];
          combinedModuleIds = combinedModuleIds.filter(modId => !restrictedForBranches.includes(modId));
          if (process.env.NODE_ENV === 'development') {
            console.log('🏢 ModuleAccessContext: Branch Manager filter applied. Restricted modules hidden.');
          }
        }
        
        const activeFeatures = finalBusinessDoc.subscription.active_features || [];
        const pkgName = finalBusinessDoc.subscription.package_name || 'Aktif Paket';

        if (combinedModuleIds.length > 0) {
          if (process.env.NODE_ENV === 'development') {
            console.log('✨ ModuleAccessContext: Loading modules for:', pkgName, '| Count:', combinedModuleIds.length);
          }
          
          const availableModules = combinedModuleIds
            .map(id => MODULE_REGISTRY[id])
            .filter(Boolean);

          setModuleAccess({
            loading: false,
            hasSubscription: true,
            packageName: pkgName,
            packageFeatures: activeFeatures,
            userAddons: addons,
            availableModules: availableModules,
            activeModuleIds: combinedModuleIds,
            totalModules: combinedModuleIds.length,
            accessibleModules: combinedModuleIds.length
          });
          return;
        }
      }

      // --- 2. FALLBACK: Fetch via SubscriptionService (Old way / Backup) ---
      const subscriptionPath = currentBusiness.hibritPath || currentBusiness.firebase_path || currentBusiness.id;
      const subscription = await SubscriptionService.getBusinessSubscription(subscriptionPath);
      
      if (!subscription || subscription.status !== 'active') {
        setModuleAccess(prev => ({
          ...prev,
          loading: false,
          hasSubscription: false,
          activeModuleIds: ['core_access']
        }));
        return;
      }

      let packageModules = [];
      let packageName = 'Unknown Package';

      if (subscription.packageId) {
        const pkg = await FirebasePackagesService.getPackage(subscription.packageId);
        if (pkg) {
          packageName = pkg.name;
          const mapToRegistryId = (legacyId) => {
            const mapping = {
              'basic-pos': 'pos_basic', 'menu-management': 'stock_basic', 'basic-reports': 'reporting_basic',
              'inventory': 'stock_pro', 'customer-management': 'crm', 'advanced-reports': 'reporting_advanced',
              'multi-branch': 'multi_branch', 'api-access': 'api_access', 'whatsapp-integration': 'whatsapp_integration',
              'online-ordering': 'ecommerce', 'sms-notifications': 'sms_notifications',
              'marketplace-integrations': 'marketplace_integrations', 'kitchen-display': 'kitchen_display', 'qr-menu': 'qr_menu'
            };
            return mapping[legacyId] || legacyId.replace(/-/g, '_');
          };
          if (pkg.modules) packageModules = pkg.modules.map(mapToRegistryId);
        }
      }

      let allModuleIds = new Set(packageModules);
      if (subscription.addons && Array.isArray(subscription.addons)) {
        subscription.addons.forEach(addonId => allModuleIds.add(addonId));
      }
      allModuleIds.add('core_access');
      const activeModuleIds = Array.from(allModuleIds);
      const availableModules = activeModuleIds.map(id => MODULE_REGISTRY[id]).filter(Boolean);

      setModuleAccess({
        loading: false,
        hasSubscription: true,
        packageName: packageName,
        packageFeatures: subscription.features || [],
        userAddons: subscription.addons || [],
        availableModules: availableModules,
        activeModuleIds: activeModuleIds,
        totalModules: activeModuleIds.length,
        accessibleModules: activeModuleIds.length
      });
    } catch (error) {
      console.error('Error fetching module access:', error);
      setModuleAccess(prev => ({ ...prev, loading: false, hasSubscription: false, activeModuleIds: ['core_access'] }));
    }
  };

  useEffect(() => {
    fetchModuleAccess();
  }, [selectedBusiness?.id, isAuthenticated, currentUser?.email]);

  const hasModuleAccess = (moduleId) => {
    if (!moduleAccess.hasSubscription) return false;
    if (moduleAccess.activeModuleIds && moduleAccess.activeModuleIds.includes(moduleId)) return true;
    return moduleAccess.availableModules.some(module =>
      module.name.toLowerCase() === moduleId.toLowerCase() || module.id === moduleId
    );
  };

  const refreshModuleAccess = () => {
    fetchModuleAccess();
  };

  const value = {
    ...moduleAccess,
    hasModuleAccess,
    hasModule: hasModuleAccess, // Alias for convenience
    refreshModuleAccess
  };

  return (
    <ModuleAccessContext.Provider value={value}>
      {children}
    </ModuleAccessContext.Provider>
  );
};

// Higher-order component for module protection
export const withModuleAccess = (WrappedComponent, explicitRequiredModule) => {
  return function ModuleProtectedComponent(props) {
    const { hasModuleAccess, loading, hasSubscription } = useModuleAccess();
    const location = window.location.pathname;

    // Determine the required module (explicit prop OR from global registry)
    let requiredModules = [];
    if (explicitRequiredModule) {
      requiredModules = [explicitRequiredModule];
    } else {
      // Use the standalone config to find required modules for this path
      requiredModules = getRequiredModules(location);
    }

    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (!hasSubscription) {
      return (
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Aktif Abonelik Gerekli</h2>
          <p className="text-gray-600 mb-8">Bu modüle erişim için aktif bir aboneliğiniz olmalı.</p>
          <button
            onClick={() => window.location.href = '/landing'}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Paketleri İncele
          </button>
        </div>
      );
    }

    // Final check: User must have access to ALL required modules (if any)
    const hasAccess = requiredModules.length === 0 || requiredModules.every(m => hasModuleAccess(m));

    if (!hasAccess) {
      const displayModuleName = explicitRequiredModule || requiredModules.join(', ');
      return (
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Erişim Yetkisi Gerekli</h2>
          <p className="text-gray-600 mb-8">
            {displayModuleName} modülüne erişim için gerekli pakete veya ek modüle sahip değilsiniz.
          </p>
          <button
            onClick={() => window.location.href = '/subscription/unified'}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Paket Yükselt veya Modül Satın Al
          </button>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
};

export default ModuleAccessContext;